import { useRef, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";

const VIDEO_FADE_DURATION_MS = 80;

function toAbsoluteUrl(url) {
  if (!url || typeof url !== "string") return url;
  if (typeof window === "undefined") return url;
  return new URL(url, window.location.href).href;
}

function getMainVideoElement(mainVideoRef) {
  return mainVideoRef?.current || null;
}

/**
 * Нативный progressive MP4 для Shorty (без hls.js / Shaka).
 * Как в HLS-версии: грузим на main, если он есть, иначе на inner для preload.
 */
export default function ShortyVideo({
  playbackUrl,
  innerVideoRef,
  mainVideoRef = null,
  isMainVideoMounted = false,
  onReady = null,
  onPlaybackStart = null,
  onError = null,
  start = null,
  children,
}) {
  const [videoReady, setVideoReady] = useState(false);
  const isAttachedToMainRef = useRef(false);
  const playbackUrlRef = useRef(playbackUrl);

  useEffect(() => {
    playbackUrlRef.current = playbackUrl;
  }, [playbackUrl]);

  const attachToMainVideo = useCallback(() => {
    const mainVideoElement = getMainVideoElement(mainVideoRef);
    const inner = innerVideoRef.current;

    if (
      !mainVideoElement ||
      !videoReady ||
      !inner?.currentSrc ||
      isAttachedToMainRef.current
    ) {
      return;
    }

    try {
      mainVideoElement.style.transition = `opacity ${VIDEO_FADE_DURATION_MS}ms ease-out`;
      const resumeTime = inner.currentTime > 0 ? inner.currentTime : start || 0;
      const srcToTransfer = inner.currentSrc;

      inner.pause();
      inner.removeAttribute("src");
      inner.load();

      mainVideoElement.src = srcToTransfer;
      isAttachedToMainRef.current = true;

      const startMain = () => {
        if (playbackUrlRef.current !== playbackUrl) {
          return;
        }

        if (resumeTime > 0) {
          mainVideoElement.currentTime = resumeTime;
        }

        mainVideoElement.play().catch(() => {});
      };

      if (mainVideoElement.readyState >= 2) {
        startMain();
        return;
      }

      const onLoadedData = () => {
        mainVideoElement.removeEventListener("loadeddata", onLoadedData);
        startMain();
      };

      mainVideoElement.addEventListener("loadeddata", onLoadedData);
    } catch (err) {
      isAttachedToMainRef.current = false;
      if (onError) onError(err);
    }
  }, [mainVideoRef, videoReady, innerVideoRef, start, onError, playbackUrl]);

  const detachFromMainVideo = useCallback(() => {
    const mainVideoElement = getMainVideoElement(mainVideoRef);

    if (!mainVideoElement || !isAttachedToMainRef.current) {
      return;
    }

    try {
      mainVideoElement.style.opacity = "0";
      if (!mainVideoElement.paused) {
        mainVideoElement.pause();
      }

      mainVideoElement.removeAttribute("src");
      mainVideoElement.load();
      isAttachedToMainRef.current = false;
    } catch (err) {
      isAttachedToMainRef.current = false;
    }
  }, [mainVideoRef]);

  useEffect(() => {
    if (!playbackUrl || !innerVideoRef.current) return;

    let mounted = true;
    setVideoReady(false);
    isAttachedToMainRef.current = false;

    const inner = innerVideoRef.current;
    const mainVideoElement = getMainVideoElement(mainVideoRef);
    const targetVideo = mainVideoElement || inner;
    const isUsingMain = !!mainVideoElement;
    const absoluteUrl = toAbsoluteUrl(playbackUrl);
    const isCurrentPlayback = () => playbackUrlRef.current === playbackUrl;

    if (inner._shortyReadyHandler) {
      inner.removeEventListener("canplay", inner._shortyReadyHandler);
      inner.removeEventListener("loadeddata", inner._shortyReadyHandler);
      inner._shortyReadyHandler = null;
    }

    if (isUsingMain) {
      targetVideo.style.transition = `opacity ${VIDEO_FADE_DURATION_MS}ms ease-out`;
      targetVideo.style.opacity = "0";
      inner.removeAttribute("src");
      inner.load();
    } else {
      targetVideo.removeAttribute("src");
      targetVideo.load();
    }

    targetVideo.src = absoluteUrl;
    if (!isUsingMain) {
      // Preload-видео всегда mute, чтобы не было фонового звука.
      targetVideo.muted = true;
    }

    let readyCalled = false;

    const markReady = () => {
      if (!mounted || readyCalled) return;
      readyCalled = true;

      if (start && targetVideo.currentTime !== start) {
        targetVideo.currentTime = start;
      }

      setVideoReady(true);

      if (isUsingMain) {
        isAttachedToMainRef.current = true;
        targetVideo.play().catch(() => {});
      } else {
        targetVideo.play().catch(() => {});
      }

      if (onReady) onReady({ playbackUrl });

      targetVideo.removeEventListener("canplay", markReady);
      targetVideo.removeEventListener("loadeddata", markReady);
      inner._shortyReadyHandler = null;
    };

    const handleError = () => {
      if (!mounted) return;
      if (onError) onError(new Error("Shorty MP4 playback failed"));
    };

    const handlePlaying = () => {
      if (!mounted || !isCurrentPlayback()) return;
      if (isUsingMain) {
        targetVideo.style.opacity = "1";
      }
      if (onPlaybackStart) onPlaybackStart({ playbackUrl });
    };

    inner._shortyReadyHandler = markReady;
    targetVideo.addEventListener("canplay", markReady);
    targetVideo.addEventListener("loadeddata", markReady);
    targetVideo.addEventListener("playing", handlePlaying);
    targetVideo.addEventListener("error", handleError, { once: true });

    return () => {
      mounted = false;

      if (inner._shortyReadyHandler) {
        targetVideo.removeEventListener("canplay", inner._shortyReadyHandler);
        targetVideo.removeEventListener(
          "loadeddata",
          inner._shortyReadyHandler
        );
        inner._shortyReadyHandler = null;
      }

      targetVideo.removeEventListener("playing", handlePlaying);

      detachFromMainVideo();
    };
  }, [
    playbackUrl,
    start,
    onReady,
    onPlaybackStart,
    onError,
    innerVideoRef,
    mainVideoRef,
    isMainVideoMounted,
    detachFromMainVideo,
  ]);

  useEffect(() => {
    const mainVideoElement = getMainVideoElement(mainVideoRef);

    if (mainVideoElement && videoReady && !isAttachedToMainRef.current) {
      attachToMainVideo();
    }
  }, [mainVideoRef, isMainVideoMounted, videoReady, attachToMainVideo]);

  useEffect(() => {
    return () => {
      if (isAttachedToMainRef.current) {
        detachFromMainVideo();
      }
    };
  }, [detachFromMainVideo]);

  if (typeof children === "function") {
    return children(innerVideoRef);
  }

  return null;
}

ShortyVideo.propTypes = {
  playbackUrl: PropTypes.string.isRequired,
  innerVideoRef: PropTypes.object.isRequired,
  mainVideoRef: PropTypes.object,
  isMainVideoMounted: PropTypes.bool,
  onReady: PropTypes.func,
  onPlaybackStart: PropTypes.func,
  onError: PropTypes.func,
  start: PropTypes.number,
  children: PropTypes.func.isRequired,
};
